/** Locale used for a currency when the caller does not specify one (INR uses Indian digit grouping). */
export function localeForCurrency(currency: string): string | undefined {
  return currency === 'INR' ? 'en-IN' : undefined;
}

const fmtCache = new Map<string, Intl.NumberFormat>();
function currencyFormatter(currency: string, locale: string | undefined): Intl.NumberFormat {
  const key = `${locale ?? ''}|${currency}`;
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, { style: 'currency', currency });
    fmtCache.set(key, f);
  }
  return f;
}

/** Number of minor-unit digits for an ISO currency (INR 2, JPY 0, KWD 3). */
export function currencyDigits(currency: string): number {
  return currencyFormatter(currency, 'en').resolvedOptions().maximumFractionDigits ?? 2;
}

/** Format integer minor units (e.g. paise) as a currency string. */
export function formatMoney(minor: number, currency: string, locale?: string): string {
  const digits = currencyDigits(currency);
  const loc = locale ?? localeForCurrency(currency);
  return currencyFormatter(currency, loc).format(minor / 10 ** digits);
}

/**
 * Parse a user-entered major-unit amount ("1,25,000.50") into integer minor units without
 * floating-point rounding. Returns null when the text is not a valid non-negative amount.
 */
export function parseMoneyInput(text: string, currency: string): number | null {
  const digits = currencyDigits(currency);
  const clean = text.replace(/[\s,]/g, '');
  const m = /^(\d+)(?:\.(\d*))?$/.exec(clean);
  if (!m) return null;
  const frac = m[2] ?? '';
  if (frac.length > digits) return null;
  const value = Number(m[1]) * 10 ** digits + Number(frac.padEnd(digits, '0') || '0');
  return Number.isSafeInteger(value) ? value : null;
}

/** Inverse of parseMoneyInput, for pre-filling inputs (no grouping, no symbol). */
export function minorToInput(minor: number | null | undefined, currency: string): string {
  if (minor === null || minor === undefined) return '';
  const digits = currencyDigits(currency);
  if (digits === 0) return String(minor);
  const s = String(Math.abs(minor)).padStart(digits + 1, '0');
  return `${minor < 0 ? '-' : ''}${s.slice(0, -digits)}.${s.slice(-digits)}`;
}

export function formatNumber(n: number, locale?: string): string {
  return new Intl.NumberFormat(locale).format(n);
}

/** Basis points → percentage text (1250 → "12.5%"). */
export function formatBp(bp: number): string {
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 }).format(bp / 100)}%`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(v)} ${units[i]}`;
}

export interface DateFormatOptions {
  withTime?: boolean;
  timeZone?: string | undefined;
  locale?: string | undefined;
}

/** Format an ISO date (YYYY-MM-DD) or timestamp. Plain dates are never shifted across time zones. */
export function formatDate(value: string, opts: DateFormatOptions = {}): string {
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  const d = new Date(dateOnly ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  const fmt = new Intl.DateTimeFormat(opts.locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...(opts.withTime && !dateOnly ? { hour: '2-digit', minute: '2-digit' } : {}),
    timeZone: dateOnly ? 'UTC' : opts.timeZone,
  });
  return fmt.format(d);
}

/** Humanise snake/dot case identifiers: "tenant.user_invited" → "Tenant user invited". */
export function humanize(s: string): string {
  const t = s.replace(/[._]+/g, ' ').trim();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** "12.5" → 1250 basis points (0–100%, up to two decimals); '' → 0; invalid → null. */
export function percentTextToBp(text: string): number | null {
  const t = text.trim();
  if (!t) return 0;
  const m = /^(\d{1,3})(?:\.(\d{1,2}))?$/.exec(t);
  if (!m) return null;
  const bp = Number(m[1]) * 100 + Number((m[2] ?? '').padEnd(2, '0'));
  return bp <= 10000 ? bp : null;
}
